import React, { useState, useEffect } from 'react';
import { glossaryService } from '../services';
import { DataTable } from '../components';
import {
  useSideDialog,
  useModalDialog,
  useGlobalState,
  usePrincipal,
  useMessage,
  useStatus,
} from '../hooks';
import { Role, Asset, handleError } from '../helpers';

/**
 * The list of glossaries. Rendered as a tab of the Glossaries page, so it deliberately brings no page
 * wrapper of its own — see views/Glossaries.js.
 */
export const GlossaryList = () => {
  const [glossaries, setGlossaries] = useState([]);
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { fetchPrincipals } = usePrincipal();
  const { appendStatus } = useStatus({ setState: setGlossaries, assetCategory: Asset.Glossary });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    const promise = new Promise(resolve => {
      glossaryService
        .getAll()
        .then(res => {
          setGlossaries(() => res.glossaries);
          appendStatus();
          setLoading(false);
          resolve(res.glossaries);
        })
        .catch(err => {
          setLoading(false);
          showMessage(err, 'error');
        });
    });

    Promise.all([promise, fetchAssetCount()])
      .then(result => {
        updateFieldCount(result[0], result[1]);
        fetchPrincipals(result[0], setGlossaries);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchAssetCount = async () => {
    return new Promise(resolve => {
      glossaryService
        .getAssetCount()
        .then(res => {
          resolve(res.asset_count);
        })
        .catch(() => {
          return null;
        });
    });
  };

  const updateFieldCount = (data, statistics) => {
    let statistic = {};

    let maxValue = 0;

    statistics.forEach(row => {
      if (row.asset_count > maxValue) {
        maxValue = row.asset_count;
      }
    });

    setGlossaries(prev =>
      prev.map(item => {
        statistic = statistics.find(x => x.glossary_id === item.glossary_id);
        return {
          ...item,
          asset_count: statistic ? statistic.asset_count : 0,
          asset_count_max: maxValue,
        };
      })
    );
  };

  const addGlossary = async data => {
    const glossaries = { glossaries: Array.isArray(data) ? data : [data] };

    await glossaryService
      .create(glossaries)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editGlossary = async data => {
    const glossaries = { glossaries: Array.isArray(data) ? data : [data] };

    await glossaryService
      .update(glossaries)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteGlossary = async data => {
    await glossaryService
      .destroy({ glossaries: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;

  return (
    <DataTable
      columns={[
        {
          id: 'glossary_name',
          name: 'Name',
          link: '/glossaries/:glossary_id',
          tooltip: 'glossary_description',
          className: 'bold',
        },
        { id: 'glossary_type', name: 'Type', className: 'small-text' },
        {
          id: 'glossary_description',
          name: 'Description',
          type: 'description',
          viewMode: 'descriptions',
        },
        {
          // Both kinds of asset a glossary holds, since counting only entities understates it.
          id: 'asset_count',
          name: 'Assets',
          type: 'chart',
          chartType: 'bar',
          data: { data: 'asset_count', max: 'asset_count_max' },
          viewMode: 'statistics',
        },
        ...(settings.SHOW_GLOSSARY_STATUSES
          ? [
              {
                id: 'status_name',
                name: 'Status',
                type: 'pill',
                value: 'status_id',
                values: statuses,
                tooltip: 'status_description',
              },
            ]
          : []),
        {
          id: 'principal_name',
          name: 'Owner',
          type: 'owner',
          link: '/:principal_url',
          data: { photo: 'principal_photo' },
          tooltipSubtitle: 'principal_type_name',
        },
      ]}
      buttons={[
        {
          label: '+ Add',
          action: 'add',
          mainButton: true,
          onClick: items => showSideDialog('addGlossary', items || selectedItems, addGlossary),
        },
        {
          label: 'Edit',
          action: 'edit',
          onClick: items => showSideDialog('editGlossary', items || selectedItems, editGlossary),
        },
        {
          label: 'Delete',
          action: 'delete',
          onClick: items =>
            showModalDialog('deleteGlossary', items || selectedItems, deleteGlossary),
        },
      ]}
      data={glossaries}
      idColumn="glossary_id"
      selectedItems={selectedItems}
      setSelectedItems={setSelectedItems}
      loading={loading}
      editable={editable}
      filterable={true}
      filterObjects={[
        'glossary_name',
        'glossary_type',
        'glossary_description',
        'principal_name',
        ...(settings.SHOW_GLOSSARY_STATUSES ? ['status_name'] : []),
      ]}
      showViewModes={true}
    />
  );
};
